import axios from "axios";
import axiosInstance from "@/config/axiosInstance";
import { getRtEventApiRoute } from "@/helpers/api.util";

export const RTEM_IP_INFO_TIMEOUT_MS = 1_500;
export const IP_API_FALLBACK_TIMEOUT_MS = 2_000;
export const IP_API_FALLBACK_URL =
  "https://demo.ip-api.com/json/?fields=66842623&lang=en";

const readPrimaryIpInfo = (response) => {
  const ipInfo = response.data?.data ?? response.data;

  if (!ipInfo || typeof ipInfo.isIran !== "boolean") {
    throw new Error("Invalid IP information response");
  }

  return ipInfo;
};

const readFallbackIpInfo = (response) => {
  const ipInfo = response.data;
  const countryIso = ipInfo?.countryCode?.toUpperCase();

  if (ipInfo?.status !== "success" || !countryIso) {
    throw new Error(ipInfo?.message || "Invalid fallback IP response");
  }

  return {
    ...ipInfo,
    ip: ipInfo.query,
    countryIso,
    isIran: countryIso === "IR",
    source: "ip-api",
  };
};

const getFallbackIpInfo = async ({ signal } = {}) => {
  // Use plain axios here. The shared client attaches the Manabakh bearer token,
  // which must never be sent to a third-party service.
  const response = await axios.get(IP_API_FALLBACK_URL, {
    signal,
    timeout: IP_API_FALLBACK_TIMEOUT_MS,
    withCredentials: false,
    headers: {
      Accept: "application/json",
    },
  });

  return readFallbackIpInfo(response);
};

const getPrimaryIpInfo = async ({ signal } = {}) => {
  const response = await axiosInstance.get(getRtEventApiRoute("ip-info"), {
    signal,
    timeout: RTEM_IP_INFO_TIMEOUT_MS,
  });

  return readPrimaryIpInfo(response);
};

const firstSuccessful = (requests) =>
  new Promise((resolve, reject) => {
    const errors = new Array(requests.length);
    let pending = requests.length;

    requests.forEach((request, index) => {
      Promise.resolve(request).then(resolve, (error) => {
        errors[index] = error;
        pending -= 1;

        // A fast failure must not beat a slower valid classification. If every
        // source fails, prefer the first-party error for Redux diagnostics.
        if (pending === 0) reject(errors[0] || errors[1]);
      });
    });
  });

/**
 * Race RT Event Manager against an external classification of the browser's
 * current public egress address. The first valid response wins. The shared
 * client is used only for RTEM so the user's Manabakh credentials never leave
 * our API boundary.
 */
export const getMyIpInfo = ({ signal } = {}) =>
  firstSuccessful([
    getPrimaryIpInfo({ signal }),
    getFallbackIpInfo({ signal }),
  ]);
